import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { getBookingSummary } from "@/lib/booking-summary.functions";
import { usd } from "@/lib/pricing";
import { track } from "@/lib/analytics";

const searchSchema = z.object({
  booking_id: fallback(z.string().optional(), undefined),
  session_id: fallback(z.string().optional(), undefined),
});

export const Route = createFileRoute("/booking/success")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({ meta: [{ title: "Booking received — Creed Car Rental" }] }),
  component: Success,
});

function Success() {
  const { booking_id } = Route.useSearch();
  const [booking, setBooking] = useState<any>(null);
  const [car, setCar] = useState<any>(null);
  const fetchSummary = useServerFn(getBookingSummary);
  const [tracked, setTracked] = useState(false);


  useEffect(() => {
    if (!booking_id) return;
    let cancelled = false;
    let attempts = 0;
    // Poll briefly so the page reflects the webhook-applied 'paid' status
    // even if Stripe delivers the event a few seconds after redirect.
    const poll = () => {
      fetchSummary({ data: { booking_id } })
        .then((res) => {
          if (cancelled) return;
          setBooking(res.booking);
          setCar(res.car);
          const b: any = res.booking;
          if (b && b.status !== "pending_payment" && !tracked) {
            setTracked(true);
            track("purchase", {
              transaction_id: b.id,
              value: Number(b.total) || 0,
              currency: "USD",
              items: res.car ? [{
                item_name: `${res.car.year} ${res.car.make} ${res.car.model}`,
                item_brand: res.car.make,
                price: Number(b.total) || 0,
                quantity: 1,
              }] : [],
            });
          }
          if (b && b.status === "pending_payment" && attempts < 5) {
            attempts += 1;
            setTimeout(poll, 1500);
          }
        })
        .catch(() => {});

    };
    poll();
    return () => { cancelled = true; };
  }, [booking_id, fetchSummary]);

  const isPaid = booking && booking.status !== "pending_payment";

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-6 py-24 text-center">
        <CheckCircle2 className="mx-auto h-16 w-16 text-accent" />
        <h1 className="mt-6 font-display text-4xl text-espresso">
          {isPaid ? "Payment received" : "Booking received"}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {isPaid
            ? "Thanks for choosing Creed Car Rental. Your payment is captured and your booking is in our review queue — the owner will confirm pickup details within a few hours. If we can't honor your dates, you'll be refunded in full."
            : "We're finalizing your payment. This page will update in a moment."}
        </p>
        {booking_id && <div className="mt-4 text-xs text-muted-foreground">Request #{booking_id.slice(0, 8).toUpperCase()}</div>}

        {booking && (
          <div className="mt-8 rounded-lg border border-border bg-card p-6 text-left">
            <div className="text-xs uppercase tracking-[0.2em] text-espresso/50">Your booking</div>
            <div className="mt-3 space-y-2 text-sm">
              {car && <Row label="Vehicle" value={`${car.year} ${car.make} ${car.model}`} />}
              <Row label="Dates" value={`${booking.start_date} → ${booking.end_date} · ${booking.days} day${booking.days===1?"":"s"}`} />
              <Row label="Total charged" value={usd(Number(booking.total))} bold />
              <Row label="Status" value={isPaid ? "Paid · awaiting owner approval" : "Awaiting payment confirmation"} />
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Save this page or take a screenshot for your records.</p>
          </div>
        )}

        <Button asChild className="mt-8 bg-primary hover:bg-primary/90"><Link to="/fleet">Browse more cars</Link></Button>
      </div>
      <SiteFooter />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? "font-display text-espresso" : "text-espresso"}>{value}</span>
    </div>
  );
}
